import React, { useState, useEffect } from 'react';
import { Chart } from 'react-google-charts';
import Badge from 'react-bootstrap/Badge';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import Card from 'react-bootstrap/Card';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Alert from 'react-bootstrap/Alert';
import Tooltip from '../components/Tooltip.js';
import AlgoDrawer from '../algos/AlgoDrawer.js';
import AlgoStatsTooltip from '../components/AlgoStatsTooltip.js';
import AlgoQuickManage from '../manage/AlgoQuickManage.js';
import SingleAlgoRobustness from '../algos/SingleAlgoRobustness.js';

function PlanDiversification(props) {
    const [allocations, setAllocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterCategory, setFilterCategory] = useState('all');

    useEffect(() => {
        let isMounted = true;
        (async function () {
            if (!props.planId) return;
            setLoading(true);
            setError(null);
            try {
                const res = await fetch(`/api/algos?planId=${props.planId}&years=0`);
                const data = await res.json();
                if (isMounted) {
                    setAllocations(Array.isArray(data) ? data : []);
                }
            } catch (err) {
                console.error('Failed to load plan algos for diversification:', err);
                if (isMounted) setError(err.message || 'Failed to load plan algorithms');
            } finally {
                if (isMounted) setLoading(false);
            }
        })();
        return () => { isMounted = false; };
    }, [props.planId]);

    // -------------------------------------------------------------
    // DIVERSIFICATION METRICS & SCORING COMPUTATION
    // -------------------------------------------------------------
    const totalAlloc = allocations.reduce((sum, a) => sum + (Number(a.percentage) || 1), 0) || 1;
    const totalAlgos = allocations.length;

    // 1. Underlying Symbols Traded (symbol NOT trade_symbol)
    const symbolMap = {};
    allocations.forEach(a => {
        const sym = String(a.symbol || a.trade_symbol || 'Unknown').trim().toUpperCase();
        if (!symbolMap[sym]) {
            symbolMap[sym] = { symbol: sym, count: 0, alloc: 0, tradeSymbols: new Set() };
        }
        symbolMap[sym].count++;
        symbolMap[sym].alloc += (Number(a.percentage) || 1);
        if (a.trade_symbol) symbolMap[sym].tradeSymbols.add(a.trade_symbol);
    });
    const symbolList = Object.values(symbolMap).sort((a, b) => b.alloc - a.alloc);
    const uniqueSymbolsCount = symbolList.length;
    const maxSymbolAlloc = symbolList.length ? Math.max(...symbolList.map(s => s.alloc)) : 0;
    const maxSymbolPct = (maxSymbolAlloc / totalAlloc) * 100;

    // Herfindahl-Hirschman Index (HHI) for symbols (0 to 1, lower = more diversified)
    const symbolHhi = symbolList.reduce((acc, s) => {
        const share = s.alloc / totalAlloc;
        return acc + share * share;
    }, 0);

    // Symbols Score (0 - 100):
    // Breadth (up to 40 pts), Concentration (up to 40 pts), Balance/HHI (up to 20 pts)
    let symbolScore = 0;
    if (uniqueSymbolsCount >= 6) symbolScore += 40;
    else if (uniqueSymbolsCount >= 4) symbolScore += 32;
    else if (uniqueSymbolsCount >= 3) symbolScore += 24;
    else if (uniqueSymbolsCount >= 2) symbolScore += 16;
    else if (uniqueSymbolsCount === 1) symbolScore += 5;

    if (maxSymbolPct <= 25) symbolScore += 40;
    else if (maxSymbolPct <= 35) symbolScore += 30;
    else if (maxSymbolPct <= 45) symbolScore += 18;
    else if (maxSymbolPct <= 60) symbolScore += 8;
    else symbolScore += 0;

    symbolScore += Math.max(0, Math.round((1 - symbolHhi) * 20));
    symbolScore = Math.min(100, Math.max(0, symbolScore));

    // 2. Long vs Short Direction
    let longAlloc = 0;
    let shortAlloc = 0;
    let longCount = 0;
    let shortCount = 0;

    allocations.forEach(a => {
        const isShort = a.short === 1 || a.short === true || a.short === '1' ||
            String(a.trade_symbol || '').trim().toUpperCase() === 'SQQQ' ||
            String(a.trade_symbol || '').trim().toUpperCase() === 'SPXU';
        const weight = Number(a.percentage) || 1;
        if (isShort) {
            shortAlloc += weight;
            shortCount++;
        } else {
            longAlloc += weight;
            longCount++;
        }
    });

    const longPct = (longAlloc / totalAlloc) * 100;
    const shortPct = (shortAlloc / totalAlloc) * 100;

    // Direction Score (0 - 100):
    // Sweet spot for equity portfolios is 15% - 35% short hedging
    let directionScore = 50;
    if (shortPct >= 15 && shortPct <= 35) directionScore = 100;
    else if (shortPct >= 10 && shortPct < 15) directionScore = 85;
    else if (shortPct > 35 && shortPct <= 45) directionScore = 85;
    else if (shortPct >= 5 && shortPct < 10) directionScore = 70;
    else if (shortPct > 45 && shortPct <= 60) directionScore = 70;
    else if (shortPct > 0 && shortPct < 5) directionScore = 60;
    else if (shortPct === 0) directionScore = 50; // Pure long directional exposure
    else directionScore = 65; // Short-dominated

    // 3. Strategy Type (algo_type)
    const typeMap = {};
    allocations.forEach(a => {
        const t = String(a.algo_type || 'Unknown').trim();
        if (!typeMap[t]) {
            typeMap[t] = { type: t, count: 0, alloc: 0 };
        }
        typeMap[t].count++;
        typeMap[t].alloc += (Number(a.percentage) || 1);
    });
    const typeList = Object.values(typeMap).sort((a, b) => b.alloc - a.alloc);
    const uniqueTypesCount = typeList.length;
    const maxTypeAlloc = typeList.length ? Math.max(...typeList.map(t => t.alloc)) : 0;
    const maxTypePct = (maxTypeAlloc / totalAlloc) * 100;

    const typeHhi = typeList.reduce((acc, t) => {
        const share = t.alloc / totalAlloc;
        return acc + share * share;
    }, 0);

    // Strategy Type Score (0 - 100):
    let typeScore = 0;
    if (uniqueTypesCount >= 5) typeScore += 40;
    else if (uniqueTypesCount >= 4) typeScore += 34;
    else if (uniqueTypesCount >= 3) typeScore += 26;
    else if (uniqueTypesCount >= 2) typeScore += 16;
    else typeScore += 5;

    if (maxTypePct <= 30) typeScore += 40;
    else if (maxTypePct <= 40) typeScore += 30;
    else if (maxTypePct <= 55) typeScore += 18;
    else if (maxTypePct <= 70) typeScore += 8;
    else typeScore += 0;

    typeScore += Math.max(0, Math.round((1 - typeHhi) * 20));
    typeScore = Math.min(100, Math.max(0, typeScore));

    // 4. Entry Lookback Brackets (Fast <= 4, Medium 5-14, Slow > 14)
    const lookbackMap = {
        Fast: { label: 'Fast (≤ 4d)', count: 0, alloc: 0, range: '1 - 4 days' },
        Medium: { label: 'Medium (5-14d)', count: 0, alloc: 0, range: '5 - 14 days' },
        Slow: { label: 'Slow (> 14d)', count: 0, alloc: 0, range: '> 14 days' },
        Untracked: { label: 'Untracked', count: 0, alloc: 0, range: 'Null / Unassigned' }
    };

    allocations.forEach(a => {
        const weight = Number(a.percentage) || 1;
        if (a.entry_lookback == null || isNaN(Number(a.entry_lookback))) {
            lookbackMap.Untracked.count++;
            lookbackMap.Untracked.alloc += weight;
        } else {
            const lb = Number(a.entry_lookback);
            if (lb <= 4) {
                lookbackMap.Fast.count++;
                lookbackMap.Fast.alloc += weight;
            } else if (lb <= 14) {
                lookbackMap.Medium.count++;
                lookbackMap.Medium.alloc += weight;
            } else {
                lookbackMap.Slow.count++;
                lookbackMap.Slow.alloc += weight;
            }
        }
    });

    const activeLookbackBrackets = [lookbackMap.Fast, lookbackMap.Medium, lookbackMap.Slow].filter(b => b.alloc > 0).length;
    const untrackedLookbackPct = (lookbackMap.Untracked.alloc / totalAlloc) * 100;

    // Entry Lookback Score (0 - 100):
    let lookbackScore = 0;
    if (activeLookbackBrackets === 3) lookbackScore = 95;
    else if (activeLookbackBrackets === 2) lookbackScore = 78;
    else if (activeLookbackBrackets === 1) lookbackScore = 52;
    else lookbackScore = 40;

    // Slight penalty if > 40% untracked
    if (untrackedLookbackPct > 40) {
        lookbackScore = Math.max(30, lookbackScore - 12);
    }

    // 5. Average Duration Brackets (Scalp <= 3, Swing 4-15, Trend > 15)
    const durationMap = {
        Scalp: { label: 'Scalp (≤ 3d)', count: 0, alloc: 0, range: '1 - 3 days' },
        Swing: { label: 'Swing (4-15d)', count: 0, alloc: 0, range: '4 - 15 days' },
        Trend: { label: 'Trend (> 15d)', count: 0, alloc: 0, range: '> 15 days' },
        Untracked: { label: 'Untracked', count: 0, alloc: 0, range: 'Null / No Trades' }
    };

    allocations.forEach(a => {
        const weight = Number(a.percentage) || 1;
        if (a.avg_duration == null || isNaN(Number(a.avg_duration)) || Number(a.avg_duration) <= 0) {
            durationMap.Untracked.count++;
            durationMap.Untracked.alloc += weight;
        } else {
            const dur = Number(a.avg_duration);
            if (dur <= 3) {
                durationMap.Scalp.count++;
                durationMap.Scalp.alloc += weight;
            } else if (dur <= 15) {
                durationMap.Swing.count++;
                durationMap.Swing.alloc += weight;
            } else {
                durationMap.Trend.count++;
                durationMap.Trend.alloc += weight;
            }
        }
    });

    const activeDurationBrackets = [durationMap.Scalp, durationMap.Swing, durationMap.Trend].filter(b => b.alloc > 0).length;

    // Avg Duration Score (0 - 100):
    let durationScore = 0;
    if (activeDurationBrackets === 3) durationScore = 95;
    else if (activeDurationBrackets === 2) durationScore = 80;
    else if (activeDurationBrackets === 1) durationScore = 52;
    else durationScore = 40;

    // 6. Overall Composite Score (0 - 100)
    // Weights: Symbols 25%, Type 25%, Long/Short 20%, Lookback 15%, Duration 15%
    const overallScore = Math.round(
        symbolScore * 0.25 +
        typeScore * 0.25 +
        directionScore * 0.20 +
        lookbackScore * 0.15 +
        durationScore * 0.15
    );

    function getRatingBadge(score) {
        if (score >= 85) return <Badge bg="success" className="px-2 py-1 fs-6">Excellent ({score}/100)</Badge>;
        if (score >= 70) return <Badge bg="primary" className="px-2 py-1 fs-6">Good ({score}/100)</Badge>;
        if (score >= 50) return <Badge bg="warning" text="dark" className="px-2 py-1 fs-6">Fair ({score}/100)</Badge>;
        return <Badge bg="danger" className="px-2 py-1 fs-6">Poor ({score}/100)</Badge>;
    }

    function getProgressVariant(score) {
        if (score >= 85) return 'success';
        if (score >= 70) return 'primary';
        if (score >= 50) return 'warning';
        return 'danger';
    }

    // Chart Data Generators
    function getSymbolChartData() {
        const rows = [['Base Symbol', 'Allocation %']];
        symbolList.forEach(s => {
            rows.push([s.symbol, Number(((s.alloc / totalAlloc) * 100).toFixed(1))]);
        });
        return rows;
    }

    function getDirectionChartData() {
        return [
            ['Direction', 'Allocation %'],
            ['Long', Number(longPct.toFixed(1))],
            ['Short (Hedge)', Number(shortPct.toFixed(1))]
        ];
    }

    function getTypeChartData() {
        const rows = [['Strategy Type', 'Allocation %']];
        typeList.forEach(t => {
            rows.push([t.type, Number(((t.alloc / totalAlloc) * 100).toFixed(1))]);
        });
        return rows;
    }

    function getLookbackChartData() {
        return [
            ['Lookback Bracket', 'Allocation %'],
            ['Fast (≤4d)', Number(((lookbackMap.Fast.alloc / totalAlloc) * 100).toFixed(1))],
            ['Medium (5-14d)', Number(((lookbackMap.Medium.alloc / totalAlloc) * 100).toFixed(1))],
            ['Slow (>14d)', Number(((lookbackMap.Slow.alloc / totalAlloc) * 100).toFixed(1))],
            ['Untracked', Number(((lookbackMap.Untracked.alloc / totalAlloc) * 100).toFixed(1))]
        ];
    }

    function getDurationChartData() {
        return [
            ['Duration Bracket', 'Allocation %'],
            ['Scalp (≤3d)', Number(((durationMap.Scalp.alloc / totalAlloc) * 100).toFixed(1))],
            ['Swing (4-15d)', Number(((durationMap.Swing.alloc / totalAlloc) * 100).toFixed(1))],
            ['Trend (>15d)', Number(((durationMap.Trend.alloc / totalAlloc) * 100).toFixed(1))],
            ['Untracked', Number(((durationMap.Untracked.alloc / totalAlloc) * 100).toFixed(1))]
        ];
    }

    // Filter candidate list for inventory table
    const filteredAlgos = allocations.filter(a => {
        if (filterCategory === 'all') return true;
        if (filterCategory === 'long') return !(a.short === 1 || a.short === true || a.short === '1');
        if (filterCategory === 'short') return (a.short === 1 || a.short === true || a.short === '1');
        if (filterCategory === 'fast') return a.entry_lookback != null && Number(a.entry_lookback) <= 4;
        if (filterCategory === 'slow') return a.entry_lookback != null && Number(a.entry_lookback) > 14;
        return true;
    });

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">Analyzing plan diversification across symbols, directions, types, lookbacks, and durations...</p>
            </div>
        );
    }

    if (error) {
        return <Alert variant="danger">Error loading diversification analysis: {error}</Alert>;
    }

    if (!allocations.length) {
        return (
            <Alert variant="info" className="text-center my-4">
                No active algorithms allocated to Plan #{props.planId}. Allocate algorithms to view diversification analysis.
            </Alert>
        );
    }

    return (
        <div className="plan-diversification-container">
            {/* HERO SCORE BANNER */}
            <Card className="mb-4 shadow-sm border-0 bg-light">
                <Card.Body className="p-4">
                    <Row className="align-items-center">
                        <Col lg={4} className="text-center text-lg-start border-end-lg pe-lg-4 mb-3 mb-lg-0">
                            <div className="text-uppercase tracking-wider text-muted small fw-bold mb-1">
                                Plan Diversification Score
                            </div>
                            <div className="d-flex align-items-baseline justify-content-center justify-content-lg-start gap-2 mb-2">
                                <span className="display-4 fw-bold text-dark font-monospace">{overallScore}</span>
                                <span className="text-muted fs-5">/ 100</span>
                                {getRatingBadge(overallScore)}
                            </div>
                            <ProgressBar
                                now={overallScore}
                                variant={getProgressVariant(overallScore)}
                                style={{ height: '8px' }}
                                className="mb-2"
                            />
                            <div className="small text-muted">
                                Evaluated across <strong>{totalAlgos}</strong> algorithms totaling <strong>{totalAlloc}%</strong> allocation.
                            </div>
                        </Col>

                        <Col lg={8} className="ps-lg-4">
                            <div className="text-muted small fw-bold text-uppercase mb-2">Category Scorecard</div>
                            <Row className="g-2 text-center">
                                <Col xs={6} md>
                                    <div className="p-2 border rounded bg-white h-100 shadow-xs">
                                        <div className="small text-muted mb-1">🔀 Symbols</div>
                                        <div className="fw-bold fs-5 text-dark font-monospace">{symbolScore}</div>
                                        <ProgressBar now={symbolScore} variant={getProgressVariant(symbolScore)} style={{ height: '4px' }} className="my-1" />
                                        <span className="badge bg-light text-dark small">{uniqueSymbolsCount} Base Symbols</span>
                                    </div>
                                </Col>
                                <Col xs={6} md>
                                    <div className="p-2 border rounded bg-white h-100 shadow-xs">
                                        <div className="small text-muted mb-1">⚖️ Long / Short</div>
                                        <div className="fw-bold fs-5 text-dark font-monospace">{directionScore}</div>
                                        <ProgressBar now={directionScore} variant={getProgressVariant(directionScore)} style={{ height: '4px' }} className="my-1" />
                                        <span className="badge bg-light text-dark small">{shortPct.toFixed(0)}% Short Hedge</span>
                                    </div>
                                </Col>
                                <Col xs={6} md>
                                    <div className="p-2 border rounded bg-white h-100 shadow-xs">
                                        <div className="small text-muted mb-1">🧩 Strategy Type</div>
                                        <div className="fw-bold fs-5 text-dark font-monospace">{typeScore}</div>
                                        <ProgressBar now={typeScore} variant={getProgressVariant(typeScore)} style={{ height: '4px' }} className="my-1" />
                                        <span className="badge bg-light text-dark small">{uniqueTypesCount} Types</span>
                                    </div>
                                </Col>
                                <Col xs={6} md>
                                    <div className="p-2 border rounded bg-white h-100 shadow-xs">
                                        <div className="small text-muted mb-1">⏱️ Entry Lookback</div>
                                        <div className="fw-bold fs-5 text-dark font-monospace">{lookbackScore}</div>
                                        <ProgressBar now={lookbackScore} variant={getProgressVariant(lookbackScore)} style={{ height: '4px' }} className="my-1" />
                                        <span className="badge bg-light text-dark small">{activeLookbackBrackets}/3 Brackets</span>
                                    </div>
                                </Col>
                                <Col xs={6} md>
                                    <div className="p-2 border rounded bg-white h-100 shadow-xs">
                                        <div className="small text-muted mb-1">⏳ Avg Duration</div>
                                        <div className="fw-bold fs-5 text-dark font-monospace">{durationScore}</div>
                                        <ProgressBar now={durationScore} variant={getProgressVariant(durationScore)} style={{ height: '4px' }} className="my-1" />
                                        <span className="badge bg-light text-dark small">{activeDurationBrackets}/3 Brackets</span>
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* MAIN VISUALS GRID */}
            <Row className="g-3 mb-4">
                {/* 1. SYMBOLS TRADED */}
                <Col md={6}>
                    <Card className="h-100 shadow-sm border">
                        <Card.Header className="bg-white d-flex justify-content-between align-items-center py-2">
                            <span className="fw-bold">1. Base Symbols Traded (symbol, not trade_symbol)</span>
                            {getRatingBadge(symbolScore)}
                        </Card.Header>
                        <Card.Body className="p-2">
                            <p className="text-muted small mb-2 px-2">
                                Measures underlying market asset exposure across base instruments (e.g. SPY, QQQ, XLF), avoiding false diversity from leveraged proxies (e.g. TQQQ, SPXL).
                            </p>
                            <Chart
                                chartType="PieChart"
                                width="100%"
                                height="220px"
                                data={getSymbolChartData()}
                                options={{
                                    pieHole: 0.38,
                                    chartArea: { width: '92%', height: '80%' },
                                    legend: { position: 'right', textStyle: { fontSize: 11 } },
                                    colors: ['#0d6efd', '#198754', '#ffc107', '#0dcaf0', '#6f42c1', '#fd7e14', '#20c997', '#d63384']
                                }}
                            />
                            <div className="px-2" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                <Table size="sm" bordered hover className="align-middle text-center small mb-0">
                                    <thead className="table-light sticky-top">
                                        <tr>
                                            <th>Base Symbol</th>
                                            <th>Traded Products</th>
                                            <th>Algos</th>
                                            <th>Allocation</th>
                                            <th>Share %</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {symbolList.map(s => (
                                            <tr key={s.symbol}>
                                                <td className="fw-bold font-monospace">{s.symbol}</td>
                                                <td className="small text-muted">{Array.from(s.tradeSymbols).join(', ') || s.symbol}</td>
                                                <td>{s.count}</td>
                                                <td>{s.alloc}%</td>
                                                <td>
                                                    <span className={s.alloc / totalAlloc > 0.35 ? 'text-danger fw-bold' : ''}>
                                                        {((s.alloc / totalAlloc) * 100).toFixed(1)}%
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                        <Card.Footer className="bg-light small text-muted py-2">
                            <strong>Diagnostic:</strong> {maxSymbolPct > 35 ? (
                                <span className="text-danger">Concentration warning: <strong>{symbolList[0]?.symbol}</strong> represents {maxSymbolPct.toFixed(1)}% of the plan.</span>
                            ) : (
                                <span className="text-success">Healthy symbol dispersion. Max base symbol is {maxSymbolPct.toFixed(1)}%.</span>
                            )}
                        </Card.Footer>
                    </Card>
                </Col>

                {/* 2. LONG / SHORT BALANCE */}
                <Col md={6}>
                    <Card className="h-100 shadow-sm border">
                        <Card.Header className="bg-white d-flex justify-content-between align-items-center py-2">
                            <span className="fw-bold">2. Directional Bias (Long vs. Short)</span>
                            {getRatingBadge(directionScore)}
                        </Card.Header>
                        <Card.Body className="p-2">
                            <p className="text-muted small mb-2 px-2">
                                Evaluates directional balance. Including short hedging algorithms (15%–35%) reduces drawdowns during bear markets and macro corrections.
                            </p>
                            <Chart
                                chartType="PieChart"
                                width="100%"
                                height="220px"
                                data={getDirectionChartData()}
                                options={{
                                    pieHole: 0.45,
                                    chartArea: { width: '92%', height: '80%' },
                                    legend: { position: 'right', textStyle: { fontSize: 12 } },
                                    colors: ['#198754', '#dc3545']
                                }}
                            />
                            <div className="row g-2 px-2 text-center mt-1">
                                <div className="col-6">
                                    <div className="border rounded p-2 bg-light">
                                        <div className="small text-muted">🟢 Long Exposure</div>
                                        <div className="fw-bold fs-5 text-success font-monospace">{longPct.toFixed(1)}%</div>
                                        <div className="small text-muted">{longCount} algorithms ({longAlloc}% alloc)</div>
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className="border rounded p-2 bg-light">
                                        <div className="small text-muted">🔴 Short Hedge</div>
                                        <div className="fw-bold fs-5 text-danger font-monospace">{shortPct.toFixed(1)}%</div>
                                        <div className="small text-muted">{shortCount} algorithms ({shortAlloc}% alloc)</div>
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                        <Card.Footer className="bg-light small text-muted py-2">
                            <strong>Diagnostic:</strong> {shortPct < 5 ? (
                                <span className="text-warning text-dark">
                                    ⚠️ <strong>Unhedged Long Exposure:</strong> Only {shortPct.toFixed(1)}% short exposure. Consider adding short hedging strategies to cushion drawdowns.
                                </span>
                            ) : shortPct >= 15 && shortPct <= 35 ? (
                                <span className="text-success">✓ <strong>Optimal Hedging:</strong> {shortPct.toFixed(1)}% short exposure provides balanced market protection.</span>
                            ) : (
                                <span>Current short allocation: {shortPct.toFixed(1)}%.</span>
                            )}
                        </Card.Footer>
                    </Card>
                </Col>

                {/* 3. STRATEGY TYPE */}
                <Col md={6}>
                    <Card className="h-100 shadow-sm border">
                        <Card.Header className="bg-white d-flex justify-content-between align-items-center py-2">
                            <span className="fw-bold">3. Strategy Type Diversification</span>
                            {getRatingBadge(typeScore)}
                        </Card.Header>
                        <Card.Body className="p-2">
                            <p className="text-muted small mb-2 px-2">
                                Blends structurally uncorrelated strategy engines (e.g. Mean Reversion, Trend, Momentum, Calendar, Intermarket).
                            </p>
                            <Chart
                                chartType="ColumnChart"
                                width="100%"
                                height="220px"
                                data={getTypeChartData()}
                                options={{
                                    chartArea: { width: '85%', height: '70%' },
                                    legend: { position: 'none' },
                                    colors: ['#6f42c1'],
                                    vAxis: { format: "#'%'" }
                                }}
                            />
                            <div className="px-2" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                <Table size="sm" bordered hover className="align-middle text-center small mb-0">
                                    <thead className="table-light sticky-top">
                                        <tr>
                                            <th>Strategy Type</th>
                                            <th>Algos</th>
                                            <th>Alloc %</th>
                                            <th>Share %</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {typeList.map(t => (
                                            <tr key={t.type}>
                                                <td className="fw-bold text-start ps-3">{t.type}</td>
                                                <td>{t.count}</td>
                                                <td>{t.alloc}%</td>
                                                <td>
                                                    <span className={t.alloc / totalAlloc > 0.40 ? 'text-warning fw-bold text-dark' : ''}>
                                                        {((t.alloc / totalAlloc) * 100).toFixed(1)}%
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                        <Card.Footer className="bg-light small text-muted py-2">
                            <strong>Diagnostic:</strong> {uniqueTypesCount >= 4 ? (
                                <span className="text-success">✓ Strong strategy diversity with {uniqueTypesCount} distinct styles.</span>
                            ) : (
                                <span className="text-warning text-dark">⚠️ Only {uniqueTypesCount} strategy types represented. Try adding complementary styles (e.g. Trend or Momentum).</span>
                            )}
                        </Card.Footer>
                    </Card>
                </Col>

                {/* 4. ENTRY LOOKBACK */}
                <Col md={6}>
                    <Card className="h-100 shadow-sm border">
                        <Card.Header className="bg-white d-flex justify-content-between align-items-center py-2">
                            <span className="fw-bold">4. Entry Lookback Horizon</span>
                            {getRatingBadge(lookbackScore)}
                        </Card.Header>
                        <Card.Body className="p-2">
                            <p className="text-muted small mb-2 px-2">
                                Mixes entry speeds across Fast (≤4 bars), Medium (5-14 bars), and Slow (>14 bars) to avoid simultaneous trade clustering.
                            </p>
                            <Chart
                                chartType="ColumnChart"
                                width="100%"
                                height="220px"
                                data={getLookbackChartData()}
                                options={{
                                    chartArea: { width: '85%', height: '70%' },
                                    legend: { position: 'none' },
                                    colors: ['#0dcaf0'],
                                    vAxis: { format: "#'%'" }
                                }}
                            />
                            <div className="px-2">
                                <Table size="sm" bordered hover className="align-middle text-center small mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Lookback Bracket</th>
                                            <th>Lookback Range</th>
                                            <th>Algos</th>
                                            <th>Share %</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.entries(lookbackMap).map(([k, b]) => (
                                            <tr key={k}>
                                                <td className="fw-bold">{b.label}</td>
                                                <td className="text-muted">{b.range}</td>
                                                <td>{b.count}</td>
                                                <td>{((b.alloc / totalAlloc) * 100).toFixed(1)}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                        <Card.Footer className="bg-light small text-muted py-2">
                            <strong>Diagnostic:</strong> {lookbackMap.Untracked.count > 0 ? (
                                <span className="text-muted">{lookbackMap.Untracked.count} algos ({untrackedLookbackPct.toFixed(0)}%) have untracked lookback. Active spread is across {activeLookbackBrackets}/3 brackets.</span>
                            ) : (
                                <span className="text-success">Lookback horizon is distributed across {activeLookbackBrackets}/3 speed brackets.</span>
                            )}
                        </Card.Footer>
                    </Card>
                </Col>

                {/* 5. AVERAGE DURATION */}
                <Col md={12}>
                    <Card className="shadow-sm border">
                        <Card.Header className="bg-white d-flex justify-content-between align-items-center py-2">
                            <span className="fw-bold">5. Average Holding Duration (Trade Lifespan)</span>
                            {getRatingBadge(durationScore)}
                        </Card.Header>
                        <Card.Body className="p-3">
                            <Row className="align-items-center">
                                <Col md={5}>
                                    <p className="text-muted small mb-2">
                                        Temporal diversification ensures capital turns over at different intervals: Scalps (≤3d), Swings (4-15d), and Trend runners (>15d).
                                    </p>
                                    <Chart
                                        chartType="PieChart"
                                        width="100%"
                                        height="200px"
                                        data={getDurationChartData()}
                                        options={{
                                            pieHole: 0.4,
                                            chartArea: { width: '95%', height: '85%' },
                                            legend: { position: 'right', textStyle: { fontSize: 11 } },
                                            colors: ['#fd7e14', '#20c997', '#0d6efd', '#6c757d']
                                        }}
                                    />
                                </Col>
                                <Col md={7}>
                                    <Table size="sm" bordered hover className="align-middle text-center small mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Duration Bracket</th>
                                                <th>Typical Lifespan</th>
                                                <th>Role in Portfolio</th>
                                                <th>Algos</th>
                                                <th>Share %</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td className="fw-bold text-start ps-3">⚡ Scalp</td>
                                                <td>≤ 3 days</td>
                                                <td className="text-muted text-start ps-2">High velocity, quick mean-reversion rebalance</td>
                                                <td>{durationMap.Scalp.count}</td>
                                                <td className="fw-bold">{((durationMap.Scalp.alloc / totalAlloc) * 100).toFixed(1)}%</td>
                                            </tr>
                                            <tr>
                                                <td className="fw-bold text-start ps-3">🌊 Swing</td>
                                                <td>4 – 15 days</td>
                                                <td className="text-muted text-start ps-2">Multi-day cycle momentum & pullbacks</td>
                                                <td>{durationMap.Swing.count}</td>
                                                <td className="fw-bold">{((durationMap.Swing.alloc / totalAlloc) * 100).toFixed(1)}%</td>
                                            </tr>
                                            <tr>
                                                <td className="fw-bold text-start ps-3">📈 Trend</td>
                                                <td>> 15 days</td>
                                                <td className="text-muted text-start ps-2">Extended multi-week macro trend followers</td>
                                                <td>{durationMap.Trend.count}</td>
                                                <td className="fw-bold">{((durationMap.Trend.alloc / totalAlloc) * 100).toFixed(1)}%</td>
                                            </tr>
                                            {durationMap.Untracked.count > 0 && (
                                                <tr className="table-light text-muted">
                                                    <td className="text-start ps-3">⚪ Untracked</td>
                                                    <td>Null</td>
                                                    <td className="text-start ps-2">No historical bar duration logged</td>
                                                    <td>{durationMap.Untracked.count}</td>
                                                    <td>{((durationMap.Untracked.alloc / totalAlloc) * 100).toFixed(1)}%</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </Col>
                            </Row>
                        </Card.Body>
                        <Card.Footer className="bg-light small text-muted py-2">
                            <strong>Diagnostic:</strong> {activeDurationBrackets === 3 ? (
                                <span className="text-success">✓ Excellent temporal blend: capital turnover spans quick scalps, multi-day swings, and extended trends.</span>
                            ) : (
                                <span>Duration active across {activeDurationBrackets} brackets.</span>
                            )}
                        </Card.Footer>
                    </Card>
                </Col>
            </Row>

            {/* DETAILED ALLOCATED ALGORITHMS TABLE */}
            <Card className="shadow-sm border">
                <Card.Header className="bg-white d-flex justify-content-between align-items-center py-2">
                    <span className="fw-bold">Allocated Algorithms Inventory ({allocations.length})</span>
                    <div className="d-flex gap-1">
                        <button
                            className={`btn btn-xs ${filterCategory === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setFilterCategory('all')}
                        >
                            All ({allocations.length})
                        </button>
                        <button
                            className={`btn btn-xs ${filterCategory === 'long' ? 'btn-success' : 'btn-outline-secondary'}`}
                            onClick={() => setFilterCategory('long')}
                        >
                            Long ({longCount})
                        </button>
                        <button
                            className={`btn btn-xs ${filterCategory === 'short' ? 'btn-danger' : 'btn-outline-secondary'}`}
                            onClick={() => setFilterCategory('short')}
                        >
                            Short ({shortCount})
                        </button>
                    </div>
                </Card.Header>
                <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
                    <Table size="sm" striped bordered hover className="align-middle mb-0 text-center small">
                        <thead className="table-dark sticky-top">
                            <tr>
                                <th>#</th>
                                <th className="text-start">Algorithm Name</th>
                                <th>Base Symbol (symbol)</th>
                                <th>Trade Symbol</th>
                                <th>Direction</th>
                                <th>Strategy Type</th>
                                <th>Entry Lookback</th>
                                <th>Avg Duration</th>
                                <th>Allocation %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredAlgos.map((a, idx) => {
                                const isShort = a.short === 1 || a.short === true || a.short === '1';
                                const lb = a.entry_lookback != null ? Number(a.entry_lookback) : null;
                                const lbBracket = lb == null ? 'Untracked' : (lb <= 4 ? 'Fast' : (lb <= 14 ? 'Medium' : 'Slow'));
                                const dur = a.avg_duration != null ? Number(a.avg_duration) : null;
                                const durBracket = dur == null ? 'Untracked' : (dur <= 3 ? 'Scalp' : (dur <= 15 ? 'Swing' : 'Trend'));

                                return (
                                    <tr key={a.idalgos || idx}>
                                        <td className="text-muted">{idx + 1}</td>
                                        <td className="text-start fw-bold">
                                            <div className="d-flex align-items-center gap-1">
                                                <AlgoDrawer
                                                    link={a.display_name || a.name}
                                                    planId={props.planId}
                                                    algoId={a.idalgos}
                                                    role={props.role}
                                                />
                                                <Tooltip width="500px" textClass="tooltip-link smaller-text" text="📊">
                                                    <SingleAlgoRobustness algoId={a.idalgos} />
                                                </Tooltip>
                                                <AlgoStatsTooltip algoId={a.idalgos} years={0} />
                                                <AlgoQuickManage role={props.role} algoId={a.idalgos} planId={props.planId} />
                                            </div>
                                        </td>
                                        <td className="font-monospace fw-bold text-primary">{a.symbol || '-'}</td>
                                        <td className="font-monospace text-muted">{a.trade_symbol || a.symbol || '-'}</td>
                                        <td>
                                            {isShort ? (
                                                <Badge bg="danger">🔴 Short</Badge>
                                            ) : (
                                                <Badge bg="success">🟢 Long</Badge>
                                            )}
                                        </td>
                                        <td><Badge bg="light" text="dark" className="border">{a.algo_type || 'Unknown'}</Badge></td>
                                        <td>
                                            {lb != null ? (
                                                <span>
                                                    <strong>{lb}d</strong>{' '}
                                                    <Badge bg={lbBracket === 'Fast' ? 'info' : (lbBracket === 'Medium' ? 'primary' : 'secondary')} text={lbBracket === 'Fast' ? 'dark' : 'white'}>
                                                        {lbBracket}
                                                    </Badge>
                                                </span>
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>
                                        <td>
                                            {dur != null ? (
                                                <span>
                                                    <strong>{dur}d</strong>{' '}
                                                    <Badge bg={durBracket === 'Scalp' ? 'warning' : (durBracket === 'Swing' ? 'info' : 'primary')} text={durBracket === 'Scalp' ? 'dark' : 'white'}>
                                                        {durBracket}
                                                    </Badge>
                                                </span>
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>
                                        <td className="fw-bold font-monospace">{a.percentage}%</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            </Card>
        </div>
    );
}

export default PlanDiversification;

